import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  type NegotiationRoundDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { effectiveBidDeadline } from "../qualification/stage";
import { mustNegotiateBeforeAward } from "../rounds/partIIDialogue";

/**
 * Picking a winner is the conclusion of the evaluation, not a shortcut past it — while the event is
 * being evaluated the scores and the product basket are still moving. A negotiated competition is
 * the exception in form only: it stays in EVALUATION until it is awarded and signals a finished
 * evaluation by completing its rounds, so completed rounds count the same as a completed evaluation.
 */
const POST_EVALUATION_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluated,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
];

export const isEvaluationConcluded = (
  status: SourcingEventStatus,
  rounds: readonly NegotiationRoundDto[],
): boolean => {
  if (status === SourcingEventStatus.Evaluation) {
    const sent = rounds.filter((round) => round.status !== NegotiationRoundStatus.Draft);
    return sent.length > 0 && sent.every((round) => round.status === NegotiationRoundStatus.Completed);
  }
  return POST_EVALUATION_STATUSES.includes(status);
};

// Profiles.kt gates AWARD on BidSubmissionDeadlinePassed for every profile, early bid access or not.
export const isBidDeadlineStillOpen = (event: SourcingEventDto, now: number): boolean => {
  const deadline = effectiveBidDeadline(event);
  return deadline != null && now <= Date.parse(deadline);
};

export const canSelectWinner = (
  event: SourcingEventDto,
  rounds: readonly NegotiationRoundDto[],
  now: number,
): boolean =>
  isEvaluationConcluded(event.status, rounds) &&
  !isBidDeadlineStillOpen(event, now) &&
  !mustNegotiateBeforeAward(event, rounds);
